import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { DOMAIN_ICONS, type DomainKey } from './domain-icons';
import { IconSize, sizePx } from './icon-types';

/** Opacity of the duotone secondary layer — a soft same-hue tint behind the glyph. */
const SECONDARY_OPACITY = '0.22';

export interface DomainIconProps {
  /** Which app domain this icon represents; selects both the glyph and its color. */
  domain: DomainKey;
  size?: IconSize;
  className?: string;
  'aria-hidden'?: boolean;
}

/**
 * A duotone glyph tinted with its domain's identity color (`--domain-<key>`).
 *
 * Both FontAwesome duotone layers are driven from the single domain token: the
 * primary layer at full strength, the secondary layer at {@link SECONDARY_OPACITY}.
 * Color reservation is intentional — only domain/navigation icons are colored,
 * so color reads as "where am I," not decoration.
 */
export const DomainIcon = React.forwardRef<SVGSVGElement, DomainIconProps>(
  ({ domain, size = 'md', className = '', ...props }, ref) => {
    const px = sizePx[size];
    const color = `var(--domain-${domain})`;

    return (
      <FontAwesomeIcon
        ref={ref}
        icon={DOMAIN_ICONS[domain]}
        className={className}
        style={{
          width: px,
          height: px,
          // FA duotone: both layers from the single domain token; secondary is a soft tint.
          '--fa-primary-color': color,
          '--fa-secondary-color': color,
          '--fa-secondary-opacity': SECONDARY_OPACITY,
        }}
        aria-hidden={props['aria-hidden'] ?? true}
        data-domain={domain}
        {...props}
      />
    );
  }
);

DomainIcon.displayName = 'DomainIcon';
